import React, { useState, useEffect } from 'react';
import type { RadioChangeEvent } from 'antd';
import {
  Button,
  Col,
  Drawer,
  Form,
  Grid,
  Input,
  message,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  Upload,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useProcessStartSettings } from '../../hooks/useProcessStartSettings';
import { ProcessBlockedBanner } from '../ProcessBlockedBanner';
import { useTranslation } from 'react-i18next';
import { validateNumber } from '../../Validators/ValidatorNumber';
import {
  GetOrganizationById,
  PostInternshipOffer,
  GetStudyLines,
} from '../../helper/apiHelper';
import type {
  CompetencyTable,
  ReducedOrganization,
} from '../../Types/interfaces';
import {
  ContractType,
  InternshipOfferStatus,
  type InternshipOffer,
  type NewInternshipComponentProps,
  type StudyLine,
} from '../../Types/interfaces';
import { EditableCompetencyTable } from './EditableCompetencyTable';
import defaultCompetencies from './defaultCompetencies.json';
import { InternshipOfferType } from '../../Types/interfaces';
import { useUserOrganizations } from '../../hooks/useOrganizations';
import { useCurrentUser } from '../../hooks/useCurrentUser';

const { TextArea } = Input;
const { useBreakpoint } = Grid;

const defaultCompetencyTable: CompetencyTable = {
  competencyTableItems: defaultCompetencies,
};

const NewInternshipComponent = (
  props: NewInternshipComponentProps
): React.ReactElement => {
  const loggedInUser = useCurrentUser();
  const { internshipOfferCreationEnabled } = useProcessStartSettings();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const [isLocationDisabled, setIsLocationDisabled] = useState(true);
  const [studyLines, setStudyLines] = useState<StudyLine[]>();
  const [messageApi, contextHolder] = message.useMessage();
  const [competencyTable, setCompetencyTable] = useState<CompetencyTable>(
    defaultCompetencyTable
  );
  const [selectedOrganisation, setSelectedOrganisation] =
    useState<ReducedOrganization>();
  const { data: userOrganizations } = useUserOrganizations(loggedInUser.id);
  const [contractType, setContractType] = useState<ContractType>();
  const screens = useBreakpoint();

  useEffect(() => {
    const fetchStudyLines = async (): Promise<void> => {
      try {
        const studyLinesData: StudyLine[] = await GetStudyLines();
        setStudyLines(studyLinesData);
        if (studyLinesData.length === 1) {
          form.setFieldValue('studyLineIds', [studyLinesData[0].id]);
        }
      } catch (error) {
        messageApi.error(t('newInternshipComp.fetchError'));
        console.error('Error fetching study lines:', error);
      }

      if (userOrganizations?.length === 1) {
        form.setFieldValue('organizationId', userOrganizations[0].id);
        setSelectedOrganisation(userOrganizations[0]);
      }
    };

    fetchStudyLines();
  }, [form, userOrganizations, messageApi, t]);

  const showDrawer = (): void => {
    setOpen(true);
  };

  const handleOk = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      form.resetFields();

      const internshipOffer: InternshipOffer = {
        name: values.name,
        numberOfHours: Number(values.numberOfHours),
        numberOfPositions: Number(values.numberOfPositions),
        paid: values.paid === undefined ? false : values.paid,
        type: values.type ?? InternshipOfferType.organization,
        logo: values.logo.file.name,
        logoData: values.logo.fileList[0].thumbUrl.split(',')[1],
        organizationId: values.organizationId,
        description: values.description,
        internshipSessionIds: values.studyLineIds,
        competencyTable: competencyTable,
        city: values.city,
        street: values.street,
        streetNumber: values.streetNumber,
        status: InternshipOfferStatus.active,
        startTime: values.startDate,
        endTime: values.endDate,
        contractType: values.contractType,
        otherProjectName: values.otherProjectName || null,
      };

      if (isLocationDisabled) {
        const response = await GetOrganizationById(
          internshipOffer.organizationId.toString()
        );
        internshipOffer.city = response.city;
        internshipOffer.street = response.street;
        internshipOffer.streetNumber = response.streetNumber;
      }

      await PostInternshipOffer(internshipOffer);
      await props.onNewInternShipComponentAdded();

      messageApi.success({ content: t('newInternshipComp.addSuccess') });
      setOpen(false);
    } catch (error) {
      messageApi.error({ content: t('newInternshipComp.addError') });
      console.log(error);
    }
  };

  const handleCancel = (): void => {
    form.resetFields();
    setOpen(false);
  };

  const handleBeforeUpload = (): boolean => {
    return false;
  };

  const handleSwitchChange = (
    checked: boolean | ((prevState: boolean) => boolean)
  ): void => {
    setIsLocationDisabled(checked);
  };

  const handlAddNewInternshipOfferButton = (): void => {
    showDrawer();
  };

  const HandleContractTypeChange = (event: RadioChangeEvent): void => {
    setContractType(Number(event.target.value));
  };

  return (
    <>
      {contextHolder}
      {!internshipOfferCreationEnabled && (
        <ProcessBlockedBanner
          style={{ marginBottom: 16 }}
          message={t('processStartSettings.internshipOfferCreationBlocked')}
        />
      )}
      <Button
        size="large"
        type="primary"
        disabled={!internshipOfferCreationEnabled}
        onClick={handlAddNewInternshipOfferButton}
      >
        {t('newInternshipComp.addNewInt')}
      </Button>
      <Drawer
        title={t('newInternshipComp.addNewInt')}
        width={screens.lg ? '70vw' : screens.md ? '90vw' : '100vw'}
        open={open}
        onClose={handleCancel}
        extra={
          <Space>
            <Button onClick={handleCancel}>
              {t('newInternshipComp.cancel')}
            </Button>
            <Button type="primary" onClick={handleOk}>
              {t('newInternshipComp.submit')}
            </Button>
          </Space>
        }
      >
        <Form form={form} size="large">
          <Row>
            <Col span={24}>
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: t('newInternshipComp.inputTitle'),
                  },
                ]}
              >
                <Input placeholder={t('newInternshipComp.title')} />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Form.Item
                name="description"
                rules={[
                  { required: true, message: t('newInternshipComp.inputDesc') },
                ]}
              >
                <TextArea rows={4} placeholder={t('newInternshipComp.desc')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Typography.Text>
                {t('newInternshipComp.startDate')}:
              </Typography.Text>
              <Form.Item
                name="startDate"
                rules={[
                  {
                    required: true,
                    message: t('newInternshipComp.plsInputStart'),
                  },
                ]}
              >
                <Input type="date"></Input>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Typography.Text>
                {t('newInternshipComp.endDate')}:
              </Typography.Text>
              <Form.Item
                name="endDate"
                rules={[
                  {
                    required: true,
                    message: t('newInternshipComp.plsInputEnd'),
                  },
                ]}
              >
                <Input type="date"></Input>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="organizationId"
                rules={[
                  { required: true, message: t('newInternshipComp.selectOrg') },
                ]}
              >
                <Select
                  size="large"
                  placeholder={t('newInternshipComp.selOrg')}
                  options={userOrganizations?.map((org) => ({
                    label: org.name,
                    value: org.id,
                  }))}
                  onSelect={(value) => {
                    const org = userOrganizations?.find(
                      (org) => org.id === value
                    );
                    setSelectedOrganisation(org);
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="studyLineIds"
                rules={[
                  {
                    required: true,
                    message: t('newInternshipComp.plsSelStudyline'),
                  },
                ]}
              >
                <Select
                  size="large"
                  placeholder={t('newInternshipComp.selStudyline')}
                  mode="multiple"
                  options={studyLines?.flatMap((sl) =>
                    sl.internshipSessions?.map((s) => ({
                      label: `${sl.name} - ${s.description}`,
                      value: s.id,
                    }))
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="numberOfHours"
                rules={[
                  {
                    required: true,
                    message: t('newInternshipComp.plsInputDuration'),
                  },
                  { validator: validateNumber },
                ]}
              >
                <Input placeholder={t('newInternshipComp.nrOfHours')} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="numberOfPositions"
                rules={[
                  {
                    required: true,
                    message: t('newInternshipComp.plsInputPositions'),
                  },
                  { validator: validateNumber },
                ]}
              >
                <Input placeholder={t('newInternshipComp.nrOfPositions')} />
              </Form.Item>
            </Col>
          </Row>

          {selectedOrganisation?.isUniversityOrganization && (
            <Col xs={24} md={12} style={{ marginBottom: '30px' }}>
              <Form.Item name="type">
                <Select
                  placeholder="Internship offer type"
                  options={[
                    {
                      label: t('newInternshipComp.atUniversity'),
                      value: InternshipOfferType.atUniversity,
                    },
                    {
                      label: t('newInternshipComp.researchInternship'),
                      value: InternshipOfferType.researchGroup,
                    },
                    {
                      label: t('newInternshipComp.projectBasedInternshipOffer'),
                      value: InternshipOfferType.projectBased,
                    },
                  ]}
                />
              </Form.Item>
            </Col>
          )}

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="paid" valuePropName="checked">
                <Switch
                  checkedChildren={t('newInternshipComp.paid')}
                  unCheckedChildren={t('newInternshipComp.unpaid')}
                />
              </Form.Item>
              <Form.Item name="location" valuePropName="checked">
                <Switch
                  checkedChildren={t('newInternshipComp.atHQ')}
                  unCheckedChildren={t('newInternshipComp.elsewhere')}
                  onChange={handleSwitchChange}
                  defaultChecked
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="city"
                rules={[
                  {
                    required: !isLocationDisabled,
                    message: t('newInternshipComp.plsInputCity'),
                  },
                ]}
              >
                <Input
                  disabled={isLocationDisabled}
                  placeholder={t('newInternshipComp.city')}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="street"
                rules={[
                  {
                    required: !isLocationDisabled,
                    message: t('newInternshipComp.plsInputStreet'),
                  },
                ]}
              >
                <Input
                  disabled={isLocationDisabled}
                  placeholder={t('newInternshipComp.street')}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="streetNumber"
                rules={[
                  {
                    required: !isLocationDisabled,
                    message: t('newInternshipComp.plsInputStrNr'),
                  },
                  { validator: validateNumber },
                ]}
              >
                <Input
                  disabled={isLocationDisabled}
                  placeholder={t('newInternshipComp.strNumber')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col>
              <Form.Item
                layout="vertical"
                name="contractType"
                label={t('newInternshipComp.contractTypeTag')}
                rules={[
                  {
                    required: true,
                    message: t('newInternshipComp.radioOptionMissing'),
                  },
                ]}
              >
                <Radio.Group
                  value={contractType}
                  onChange={HandleContractTypeChange}
                >
                  <Space direction="vertical">
                    <Radio value={ContractType.employed}>
                      {t('newInternshipComp.employedLabel')}
                    </Radio>
                    <Radio value={ContractType.notEmployed}>
                      {t('newInternshipComp.notEmployedLabel')}
                    </Radio>
                    <Radio value={ContractType.ESF}>
                      {t('newInternshipComp.ESFLabel')}
                    </Radio>
                    <Space direction="horizontal">
                      <Radio value={ContractType.other}>
                        {t('newInternshipComp.otherLabel')}
                      </Radio>
                      <Form.Item
                        name="otherProjectName"
                        rules={[
                          {
                            required: contractType === ContractType.other,
                            message: t('newInternshipComp.otherLabelMissing'),
                          },
                        ]}
                      >
                        <Input
                          disabled={contractType !== ContractType.other}
                          placeholder={t(
                            'newInternshipComp.otherLabelPlaceholder'
                          )}
                        ></Input>
                      </Form.Item>
                    </Space>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="logo"
            rules={[
              { required: true, message: t('newInternshipComp.plsUpload') },
            ]}
          >
            <Upload
              accept="image/png, image/jpeg"
              beforeUpload={handleBeforeUpload}
              listType="picture-card"
              maxCount={1}
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>
                  {t('newInternshipComp.uploadLogo')}
                </div>
              </div>
            </Upload>
          </Form.Item>
          <Form.Item>
            <EditableCompetencyTable
              defaultCompetencyTable={competencyTable}
              setDefaultCompetencyTable={setCompetencyTable}
              isEditableTable={true}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default NewInternshipComponent;
